import AudioAndTranscriptBlock from '@dyl/blocks/AudioAndTranscriptBlock';
import RubricBlock from '@dyl/blocks/RubricBlock';
import SimpleTaskField from '@dyl/blocks/SimpleTaskField';
import TaskItems from '@dyl/blocks/TaskItems';
import useTaskField from '@dyl/pages/Tasks/edit/useTaskField';
import withProps from '@dyl/utils/withProps';
import { FormHelperText, FormLabel, Grid, IconButton, Tooltip, Typography } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { Restore as ResetIcon, Shuffle } from '@mui/icons-material';
import _, { partial, shuffle, times } from 'lodash';
import React, { useCallback, useMemo } from 'react';

import AnswerInput from './AnswerInput';
import DisplayOrderSection from './DisplayOrderSection';
import ChipList from "@dyl/components/ChipList";

/**
 * @template T
 * @typedef {import('@dyl/pages/Tasks/edit/useTaskField').UseTaskField<T>} UseTaskField
 */

/**
 * @typedef Answer
 * @property {string} text
 * @property {number} display_order
 * @property {boolean} is_correct_answer
 */

export const PreA1 =  withProps(TaskItems, { itemComponent: PreA1Item });
export const A1 =  withProps(TaskItems, { itemComponent: A1Item });
export const A2 = withProps(TaskItems, { itemComponent: A1Item });
const CHIPLIST_CHARACTER_LIMIT = 750;

const useItemStyles = makeStyles(theme => ({
    answerContainer: {
        display: 'flex',
        flexDirection: 'row',
        marginBottom: theme.spacing(1),
        position: 'relative',
        zIndex: 1,

        '&>:not(:last-child)': {
            marginRight: theme.spacing(1),
        },
    },
    answersDistractorsHeader: {
        display: 'block',
        marginBottom: theme.spacing(2),
    },

    displayOrderHeader: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
    },
    displayOrderHeaderText: {
        marginRight: 'auto',
    },
}));

function PreA1Item({ index }) {
    const classes = useItemStyles();
    const useField = partial(useTaskField, 'items', index);

    const [audioId, setAudioId, audioError] = useField('audio_id');

    /** @type {UseTaskField<Answer[]>} */
    const [answers, setAnswers] = useField('answers');

    const resetDisplayOrder = useCallback(() => {
        setAnswers(prev => prev.map((answer, index) => ({
            ...answer,
            display_order: index + 1,
        })));
    }, []);

    const shuffleDisplayOrder = useCallback(() => {
        setAnswers(prev => {
            const newOrder = shuffle(times(prev.length, i => i + 1));

            return prev.map((answer, index) => ({
                ...answer,
                display_order: newOrder[index],
            }));
        });
    }, []);

    const trueAnswer = useMemo(() => {
        return _(answers)
            .filter(a => a.is_correct_answer)
            .map(a => a.text)
            .join('');
    }, [answers]);

    return <>
        <Typography variant='h6'>
            {index === 0 ? 'Example' : `Item ${index}`}
        </Typography>

        <Grid container spacing={3}>
            <Grid item xs={12} sm={8}>
                <Grid container spacing={3}>

                    {index === 0 && (
                        <Grid item xs={12}>
                            <RubricBlock />
                        </Grid>
                    )}

                    <Grid item xs={12}>
                        <AudioAndTranscriptBlock
                            value={audioId}
                            onChange={id => setAudioId(id)}
                            label='Stimulus Audio'
                            error={audioError}
                            required
                        />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <SimpleTaskField
                            path={['items', index, 'stimulus_text_1']}
                            label='Label'
                            helperText='The text to be shown before the input area'
                            required
                        />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <SimpleTaskField
                            path={['items', index, 'stimulus_text_2']}
                            label='Suffix'
                            helperText={'Optional suffix to the user\'s answer'}
                        />
                    </Grid>

                    {/* ANSWER */}
                    <Grid item xs={12}>
                        <FormLabel className={classes.answersDistractorsHeader}>
                            Answer & Distractors
                        </FormLabel>
                        <div className={classes.answerContainer}>
                            {times(answers.length, answerIndex => (
                                <AnswerInput
                                    key={answerIndex}
                                    itemIndex={index}
                                    index={answerIndex}
                                />
                            ))}
                        </div>
                        <FormHelperText>
                            The cards which make up the answer, and those which are distractors.
                            Drag the divider to change which cards are answers and distractors.
                        </FormHelperText>
                    </Grid>

                    {/* TRUE ANSWER */}
                    <Grid item xs={12}>
                        <Typography variant='body1'>
                            Correct answer: {trueAnswer}
                        </Typography>
                    </Grid>
                </Grid>
            </Grid>

            {/* DISPLAY ORDER */}
            <Grid item xs={12} sm={4}>
                <div className={classes.displayOrderHeader}>
                    <FormLabel className={classes.displayOrderHeaderText}>
                        Options Display Order
                    </FormLabel>

                    <Tooltip title='Reset Display Order'>
                        <IconButton onClick={resetDisplayOrder} tabIndex={-1} size="large">
                            <ResetIcon />
                        </IconButton>
                    </Tooltip>

                    <Tooltip title='Shuffle Display Order'>
                        <IconButton onClick={shuffleDisplayOrder} tabIndex={-1} size="large">
                            <Shuffle />
                        </IconButton>
                    </Tooltip>
                </div>

                <FormHelperText>
                    Drag to rearrange. 'Answer' cards are numbered by
                    the order the user must select them in.
                </FormHelperText>

                <DisplayOrderSection itemIndex={index} />
            </Grid>
        </Grid>
    </>;
}


function A1Item({ index }) {
    const useField = partial(useTaskField, 'items', index);

    const [audioId, setAudioId, audioError] = useField('audio_id');

    // Backend uses singular 'key' as a key
    const [keys, setKeys, keysError] = useField('answers', 0, 'key');

    return <>
        <Typography variant='h6'>
            {index === 0 ? 'Example' : `Item ${index}`}
        </Typography>

        <Grid container spacing={3}>
            <Grid item xs={12} sm={8}>
                <Grid container spacing={3}>

                    {index === 0 && (
                        <Grid item xs={12}>
                            <RubricBlock />
                        </Grid>
                    )}

                    <Grid item xs={12}>
                        <AudioAndTranscriptBlock
                            value={audioId}
                            onChange={id => setAudioId(id)}
                            label='Stimulus Audio'
                            error={audioError}
                            required
                        />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <SimpleTaskField
                            path={['items', index, 'stimulus_text_1']}
                            label='Label'
                            helperText='The text to be shown before the input area'
                            required
                        />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <SimpleTaskField
                            path={['items', index, 'stimulus_text_2']}
                            label='Suffix'
                            helperText={'Optional suffix to the user\'s answer'}
                        />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <ChipList
                            label='Correct Answer(s)'
                            helperText={keysError || index === 0
                                ? 'The correct example answer which will be typed'
                                : 'The allowed correct answer(s) which the user should type one of.'}
                            error={!!keysError}
                            value={keys}
                            onChange={setKeys}
                            // Example only allows one.
                            {...(index === 0 && { max: 1 })}
                            maxLen = {CHIPLIST_CHARACTER_LIMIT}
                            isNoteTaking = {true}
                        />
                    </Grid>
                </Grid>
            </Grid>
        </Grid>
    </>;
}